import { Button, Flex, Form, Input, Select, Space, Typography, notification } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { DefaultOptionType } from 'antd/es/select'
import { FC, useEffect, useMemo } from 'react'
import extractCreateFormReorderTaskArg from '../../../helpers/tasks/extract-create-form-reorder-task'
import renderDatetime from '../../../helpers/tables/render-datetime'
import isValidateErrorEntity from '../../../helpers/type-guards/is-validate-error-entity'
import useErrorModal from '../../../hooks/api/use-error-modal'
import ColorSwatch from '../../../pages/designer-tasks/components/forms-set-orders-editor/components/color-swatch'
import { useLazyGetShipmentByDocIdQuery } from '../../../store/api/shipments-api'
import { useCreateTaskMutation } from '../../../store/api/tasks-api'
import ApiForm from '../../../types/api/form'
import { EndpointQueryParameters } from '../../../types/strapi/strapi'
import { TaskCreationFormProps } from '../types'

interface FormReorderFormValues {
    shipmentNumber?: string
    selectedFormId?: string
    description?: string
}

type FormOption = DefaultOptionType & {
    value: string
    searchText: string
    form: ApiForm
}

type FormOptionFields = Pick<
    ApiForm,
    'num' | 'color' | 'colorName' | 'failureReason' | 'dateOfFailure'
>

const FORM_REORDER_SHIPMENT_QUERY: EndpointQueryParameters = {
    populate: {
        designer: {
            fields: ['documentId'],
        },
        stamp: {
            fields: ['documentId'],
        },
        form_set: {
            populate: {
                forms: {
                    fields: [
                        'documentId',
                        'num',
                        'color',
                        'colorName',
                        'failureReason',
                        'dateOfFailure',
                    ],
                },
            },
        },
    },
}

const FormFailureDetails = ({
    form,
}: {
    form: Pick<ApiForm, 'failureReason' | 'dateOfFailure'>
}) => {
    if (!form.failureReason && !form.dateOfFailure) return null

    return (
        <Typography.Text type='secondary' style={{ textAlign: 'right' }}>
            {form.failureReason}
            {form.failureReason && form.dateOfFailure ? <br /> : null}
            {form.dateOfFailure ? renderDatetime(form.dateOfFailure) : null}
        </Typography.Text>
    )
}

const FormOptionContent = ({ form }: { form: FormOptionFields }) => (
    <Flex justify='space-between' align='center' gap={8} style={{ width: '100%' }}>
        <Space size={8} align='center'>
            <Typography.Text style={{ minWidth: 72 }}>{form.num || '—'}</Typography.Text>
            <div style={{ width: 40, flexShrink: 0 }}>
                {form.color ? <ColorSwatch $hex={form.color} /> : null}
            </div>
            <Typography.Text>{form.colorName || '—'}</Typography.Text>
        </Space>
        <FormFailureDetails form={form} />
    </Flex>
)

const FormReorderForm: FC<TaskCreationFormProps<'Перезаказ форм'>> = ({
    initialValues,
    onSuccess,
    onCancel,
}) => {
    const shipment = initialValues?.shipment
    const [form] = useForm<FormReorderFormValues>()
    const [getShipmentByDocId, getShipmentByDocIdState] = useLazyGetShipmentByDocIdQuery()
    const [createTask, createTaskState] = useCreateTaskMutation()

    useErrorModal(getShipmentByDocIdState, 'Ошибка получения данных отгрузки')
    useErrorModal(createTaskState, 'Ошибка создания задачи')

    const loading =
        getShipmentByDocIdState.isLoading ||
        getShipmentByDocIdState.isFetching ||
        createTaskState.isLoading

    const forms = useMemo(
        () => getShipmentByDocIdState.data?.data?.form_set?.forms ?? [],
        [getShipmentByDocIdState.data?.data?.form_set?.forms]
    )

    const formOptions = useMemo(
        (): FormOption[] =>
            forms.map(f => ({
                value: f.documentId,
                label: [f.num, f.colorName, f.failureReason].filter(Boolean).join(' '),
                searchText: [f.num, f.colorName, f.failureReason]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase(),
                form: f,
            })),
        [forms]
    )

    const filterFormOption = (input: string, option?: DefaultOptionType) => {
        const searchText = (option as FormOption | undefined)?.searchText ?? ''
        return searchText.includes(input.trim().toLowerCase())
    }

    useEffect(() => {
        const shipmentDocId = shipment?.documentId
        if (!shipmentDocId) return

        getShipmentByDocId([shipmentDocId, FORM_REORDER_SHIPMENT_QUERY], false)
    }, [shipment?.documentId, getShipmentByDocId])

    const onSubmit = async () => {
        try {
            const values = await form.validateFields()
            const shipmentDocId = shipment?.documentId

            if (!shipmentDocId) {
                notification.error({ message: 'Не передана отгрузка' })
                return
            }

            const fetchedShipment = getShipmentByDocIdState.data?.data

            if (!fetchedShipment) {
                notification.error({ message: 'Не удалось получить данные отгрузки' })
                return
            }

            const selectedForm = values.selectedFormId
                ? forms.find(f => f.documentId === values.selectedFormId)
                : undefined
            const description = values.description?.trim()

            if (!selectedForm && !description) {
                notification.error({
                    message:
                        'Укажите форму для перезаказа или опишите, какую форму нужно перезаказать',
                })
                return
            }

            await createTask(
                extractCreateFormReorderTaskArg(fetchedShipment, description, selectedForm)
            ).unwrap()
            notification.success({ message: 'Задача создана' })
            onSuccess()
        } catch (e) {
            if (isValidateErrorEntity(e)) return
            console.error(e)
            notification.error({ message: 'Не удалось создать задачу' })
        }
    }

    if (!shipment?.documentId) {
        return <Typography.Text type='danger'>Не передана отгрузка</Typography.Text>
    }

    return (
        <Form
            form={form}
            layout='vertical'
            initialValues={{
                shipmentNumber: shipment.id != null ? String(shipment.id) : undefined,
                selectedFormId: initialValues?.selectedFormId,
                description: initialValues?.description,
            }}
        >
            <Form.Item name='shipmentNumber' label='Номер отгрузки'>
                <Input disabled />
            </Form.Item>
            <Form.Item name='selectedFormId' label='Форма для перезаказа'>
                <Select
                    allowClear
                    showSearch
                    placeholder='Выберите форму, если известна'
                    options={formOptions}
                    filterOption={filterFormOption}
                    optionRender={option => {
                        const formItem = (option.data as FormOption).form
                        return formItem ? <FormOptionContent form={formItem} /> : option.label
                    }}
                    labelRender={props => {
                        const formItem = forms.find(f => f.documentId === props.value)
                        return formItem ? <FormOptionContent form={formItem} /> : props.label
                    }}
                    popupMatchSelectWidth={false}
                    dropdownStyle={{ minWidth: 420 }}
                    loading={getShipmentByDocIdState.isLoading || getShipmentByDocIdState.isFetching}
                    disabled={!formOptions.length}
                />
            </Form.Item>
            <Form.Item
                name='description'
                label='Описание задачи'
                rules={[
                    {
                        validator: (_, value) => {
                            const formId = form.getFieldValue('selectedFormId')
                            if (formId || value?.trim()) return Promise.resolve()
                            return Promise.reject(
                                new Error('Укажите описание, если форма не выбрана')
                            )
                        },
                    },
                ]}
            >
                <Input.TextArea
                    rows={3}
                    placeholder='Укажите номер/цвет формы для перезаказа'
                />
            </Form.Item>
            <Space>
                <Button onClick={onCancel}>Отмена</Button>
                <Button type='primary' loading={loading} onClick={onSubmit}>
                    Создать
                </Button>
            </Space>
        </Form>
    )
}

export default FormReorderForm
